import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import './GroceryManager.css';

const apiUrl = process.env.REACT_APP_API_URL || 'https://vakra.co.in/api';
const socketUrl = process.env.REACT_APP_WEBSOCKET_URL || 'https://vakra.co.in';

const CATEGORY_OPTIONS = [
  'Grains & Flour',
  'Rice & Pulses',
  'Oil & Ghee',
  'Spices',
  'Snacks',
  'Beverages',
  'Dairy',
  'Bakery',
  'Personal Care',
  'Cleaning',
  'Household',
  'Dry Fruits',
  'Baby Care',
  'Frozen Foods',
  'Others',
];

const UNIT_OPTIONS = [
  'kg',
  'gram',
  'litre',
  'ml',
  'piece',
  'packet',
  'box',
  'bag',
  'carton',
  'bottle',
  'crate',
  'sack',
];

const NAV_ITEMS = [
  { key: 'home', label: 'Home', icon: '🏠' },
  { key: 'products', label: 'Products', icon: '📦' },
  { key: 'stock', label: 'Stock', icon: '📋' },
  { key: 'settings', label: 'Settings', icon: '⚙️' },
  { key: 'offers', label: 'Offers', icon: '🏷️' },
];

const initialForm = (mode) => ({
  id: null,
  name: '',
  category: '',
  brand: '',
  description: '',
  unit_type: '',
  pack_size: '',
  stock_quantity: '',
  cost_price: '',
  pricing_type: 'fixed',
  selling_price: '',
  show_price: true,
  moq: mode === 'wholesale' ? 5 : 1,
  trend_item: false,
  is_active: true,
  image: null,
  existing_image: '',
});

const defaultSettings = {
  enable_pickup: true,
  enable_home_delivery: false,
  allow_cod: true,
  allow_online_payment: true,
  min_order_amount: 0,
  delivery_fee: 0,
  packaging_fee: 0,
  delivery_radius_km: 0,
  delivery_eta_min: 30,
  delivery_eta_max: 60,
  shop_open_time: '',
  shop_close_time: '',
};

const defaultOfferForm = {
  id: null,
  title: '',
  offer_type: 'custom',
  category: '',
  min_quantity: '',
  min_purchase_amount: '',
  discount_value: '',
  description: '',
  is_active: true,
};

function GroceryManager({ mode = 'retail', title = 'Grocery' }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);

  const [activeTab, setActiveTab] = useState('home');

  const [form, setForm] = useState(initialForm(mode));
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);

  const [search, setSearch] = useState('');
  const [showTrendingOnly, setShowTrendingOnly] = useState(false);
  const [stockQuickFilter, setStockQuickFilter] = useState('all');
  const [previewUrl, setPreviewUrl] = useState('');
  const [showMoreFields, setShowMoreFields] = useState(false);

  const [voiceSupported, setVoiceSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const [settings, setSettings] = useState(defaultSettings);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const [offers, setOffers] = useState([]);
  const [offerForm, setOfferForm] = useState(defaultOfferForm);
  const [savingOffer, setSavingOffer] = useState(false);

  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditForm, setInlineEditForm] = useState(null);
  const [inlinePreviewUrl, setInlinePreviewUrl] = useState('');
  const [savingInlineProduct, setSavingInlineProduct] = useState(false);
  const [moreEditOpen, setMoreEditOpen] = useState(false);

  const [toast, setToast] = useState('');

  const modeLabel = mode === 'wholesale' ? 'Wholesale' : 'Retail';
  const isWholesale = mode === 'wholesale';

  const showToast = (message) => {
    setToast(message);
    window.clearTimeout(window.__grocery_toast_timer);
    window.__grocery_toast_timer = window.setTimeout(() => setToast(''), 3500);
  };

  const toNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };

  const formatMoney = (value) => {
    const num = toNumber(value, 0);
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const pad2 = (value) => String(value).padStart(2, '0');

  const normalizeTimeValue = (value) => {
    const text = String(value || '').trim();
    if (!text) return '';

    const timePart = text.includes('T')
      ? text.split('T')[1]?.slice(0, 5)
      : text.slice(0, 5);

    const match = String(timePart || '').match(/^(\d{1,2}):(\d{2})/);
    if (!match) return '';

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return '';
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return '';

    return `${pad2(hour)}:${pad2(minute)}`;
  };

  const time24ToParts = (value) => {
    const safe = normalizeTimeValue(value);

    if (!safe) {
      return {
        hour: '',
        minute: '',
        period: 'AM',
      };
    }

    const [hh, mm] = safe.split(':');
    const hour24 = Number(hh);
    const minute = Number(mm);

    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12;

    return {
      hour: String(hour12),
      minute: pad2(minute),
      period,
    };
  };

  const partsTo24Time = ({ hour, minute, period }) => {
    if (!hour || !minute || !period) return '';

    let h = Number(hour);
    const m = Number(minute);

    if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
    if (h < 1 || h > 12 || m < 0 || m > 59) return '';

    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;

    return `${pad2(h)}:${pad2(m)}`;
  };

  const formatTime12 = (value) => {
    const safe = normalizeTimeValue(value);
    if (!safe) return 'Not set';

    const parts = time24ToParts(safe);
    if (!parts.hour || !parts.minute) return 'Not set';

    return `${parts.hour}:${parts.minute} ${parts.period}`;
  };

  const getShopTimingPreview = (openTime, closeTime) => {
    const openText = formatTime12(openTime);
    const closeText = formatTime12(closeTime);

    if (openText === 'Not set' && closeText === 'Not set') {
      return 'Shop timing not set';
    }

    return `${openText} - ${closeText}`;
  };

  const getImageUrl = (image) => {
    if (!image) return '';
    const value = String(image);
    if (value.startsWith('http://') || value.startsWith('https://')) return value;

    const base = process.env.REACT_APP_BASE_URL || 'https://vakra.co.in';
    return `${base}/${value.replace(/^\/+/, '')}`;
  };

  const buildProductDraft = (product) => ({
    id: product.id,
    name: product.name || '',
    category: product.category || '',
    brand: product.brand || '',
    description: product.description || '',
    unit_type: product.unit_type || '',
    pack_size: product.pack_size || '',
    stock_quantity: product.stock_quantity ?? '',
    cost_price: product.cost_price ?? '',
    pricing_type: product.pricing_type || 'fixed',
    selling_price: product.selling_price ?? '',
    show_price: Boolean(Number(product.show_price)),
    moq: product.moq ?? (mode === 'wholesale' ? 5 : 1),
    trend_item: Boolean(Number(product.trend_item)),
    is_active: Boolean(Number(product.is_active)),
    image: null,
    existing_image: product.image || product.image_url || '',
  });

  const appendProductFormData = (fd, sourceForm) => {
    fd.append('name', String(sourceForm.name || '').trim());
    fd.append('category', sourceForm.category || '');
    fd.append('brand', sourceForm.brand || '');
    fd.append('description', sourceForm.description || '');
    fd.append('unit_type', sourceForm.unit_type || '');
    fd.append('pack_size', sourceForm.pack_size || '');
    fd.append('stock_quantity', sourceForm.stock_quantity || 0);
    fd.append('cost_price', sourceForm.cost_price || '');
    fd.append('pricing_type', sourceForm.pricing_type || 'fixed');
    fd.append('selling_price', sourceForm.selling_price || '');
    fd.append('show_price', sourceForm.show_price ? '1' : '0');
    fd.append('moq', sourceForm.moq || 1);
    fd.append('trend_item', sourceForm.trend_item ? '1' : '0');
    fd.append('is_active', sourceForm.is_active ? '1' : '0');
    fd.append('grocery_mode', mode);
    fd.append('existing_image', sourceForm.existing_image || '');

    if (sourceForm.image) {
      fd.append('image', sourceForm.image);
    }
  };

  const revokeInlinePreviewIfBlob = () => {
    if (inlinePreviewUrl && inlinePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(inlinePreviewUrl);
    }
  };

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setVoiceSupported(Boolean(SpeechRecognition));
  }, []);

  useEffect(() => {
    setForm(initialForm(mode));
    setPreviewUrl('');
    setActiveTab('home');
    setSearch('');
    setShowTrendingOnly(false);
    setStockQuickFilter('all');
    setShowMoreFields(false);
    setInlineEditId(null);
    setInlineEditForm(null);
    setMoreEditOpen(false);
    revokeInlinePreviewIfBlob();
    setInlinePreviewUrl('');
    
  }, [mode]);

  useEffect(() => {
    if (!token) return;

    fetchProducts();
    fetchSettings();
    fetchOffers();
    
  }, [mode, token]);

  useEffect(() => {
    if (!token) return;

    const socket = io(socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      query: { token },
    });

    socketRef.current = socket;

    const handleStockUpdate = (payload) => {
      if (!payload) return;

      const updates = Array.isArray(payload.updates) ? payload.updates : [payload];

      const filtered = updates.filter((item) => {
        const updateMode = String(item.groceryMode || payload.groceryMode || '').toLowerCase();
        return updateMode === String(mode).toLowerCase();
      });

      if (!filtered.length) return;

      setProducts((prev) =>
        prev.map((product) => {
          const found = filtered.find((u) => Number(u.productId) === Number(product.id));
          if (!found) return product;

          return {
            ...product,
            stock_quantity: Number(found.stock_quantity || 0),
            is_active:
              found.is_active === undefined || found.is_active === null
                ? product.is_active
                : Number(found.is_active),
          };
        })
      );
    };

    socket.on('groceryStockUpdated', handleStockUpdate);
    socket.on('stockUpdated', handleStockUpdate);

    return () => {
      socket.off('groceryStockUpdated', handleStockUpdate);
      socket.off('stockUpdated', handleStockUpdate);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, mode]);

  useEffect(() => {
    if (form.image) {
      const url = URL.createObjectURL(form.image);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }

    if (form.existing_image) {
      setPreviewUrl(getImageUrl(form.existing_image));
    } else {
      setPreviewUrl('');
    }
  }, [form.image, form.existing_image]);

  const summary = useMemo(() => {
    const totalProducts = products.length;
    const activeProducts = products.filter((p) => Number(p.is_active) === 1).length;
    const lowStockProducts = products.filter((p) => toNumber(p.stock_quantity, 0) <= 10).length;
    const activeOffers = offers.filter((o) => Number(o.is_active) === 1).length;
    const totalStock = products.reduce((sum, p) => sum + toNumber(p.stock_quantity, 0), 0);

    return {
      totalProducts,
      activeProducts,
      lowStockProducts,
      activeOffers,
      totalStock,
    };
  }, [products, offers]);

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (stockQuickFilter === 'active') {
      list = list.filter((p) => Number(p.is_active) === 1);
    }

    if (stockQuickFilter === 'low') {
      list = list.filter((p) => toNumber(p.stock_quantity, 0) <= 10);
    }

    if (showTrendingOnly) {
      list = list.filter((p) => Number(p.trend_item) === 1);
    }

    const q = search.trim().toLowerCase();

    if (q) {
      list = list.filter((p) =>
        [p.name, p.category, p.brand, p.pack_size, p.unit_type].some((v) =>
          String(v || '').toLowerCase().includes(q)
        )
      );
    }

    return list;
  }, [products, search, showTrendingOnly, stockQuickFilter]);

  const lowStockProducts = useMemo(() => {
    return products
      .filter((p) => toNumber(p.stock_quantity, 0) <= 10)
      .slice(0, 6);
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);

      const response = await fetch(`${apiUrl}/grocery/products?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.error || 'Products load nahi hua.');
        return;
      }

      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('fetchProducts error:', err);
      showToast('Products fetch failed.');
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${apiUrl}/grocery-order-settings?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (response.ok) {
        setSettings({
          ...defaultSettings,
          ...data,
          enable_pickup: Boolean(Number(data.enable_pickup)),
          enable_home_delivery: Boolean(Number(data.enable_home_delivery)),
          allow_cod: Boolean(Number(data.allow_cod)),
          allow_online_payment: Boolean(Number(data.allow_online_payment)),
          shop_open_time: normalizeTimeValue(data.shop_open_time),
          shop_close_time: normalizeTimeValue(data.shop_close_time),
        });
      }
    } catch (err) {
      console.error('fetchSettings error:', err);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await fetch(`${apiUrl}/grocery-offers?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (response.ok) {
        setOffers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('fetchOffers error:', err);
    }
  };

  const resetForm = () => {
    setForm(initialForm(mode));
    setPreviewUrl('');
    setShowMoreFields(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetOfferForm = () => {
    setOfferForm(defaultOfferForm);
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'checkbox') {
      setForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    if (type === 'file') {
      const file = files?.[0] || null;

      if (file && !file.type.startsWith('image/')) {
        showToast('Sirf image upload karein.');
        return;
      }

      setForm((prev) => ({
        ...prev,
        image: file,
      }));
      return;
    }

    setForm((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      if (name === 'pricing_type') {
        if (value === 'inquiry' || value === 'hidden') {
          updated.show_price = false;
        } else {
          updated.show_price = true;
        }
      }

      return updated;
    });
  };

  const handleSettingsChange = (name, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOfferChange = (name, value) => {
    setOfferForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInlineChange = (name, value) => {
    setInlineEditForm((prev) => {
      if (!prev) return prev;

      const updated = {
        ...prev,
        [name]: value,
      };

      if (name === 'pricing_type') {
        if (value === 'inquiry' || value === 'hidden') {
          updated.show_price = false;
        } else {
          updated.show_price = true;
        }
      }

      return updated;
    });
  };

  const handleInlineFileChange = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Sirf image upload karein.');
      return;
    }

    revokeInlinePreviewIfBlob();

    const url = URL.createObjectURL(file);
    setInlinePreviewUrl(url);

    setInlineEditForm((prev) => ({
      ...prev,
      image: file,
    }));
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      showToast('Voice input is browser me support nahi hai.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || '';
      setForm((prev) => ({
        ...prev,
        name: transcript,
      }));
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognition.start();
  };

  const validateProductForm = () => {
    if (!form.name.trim()) {
      showToast('Product name required hai.');
      return false;
    }

    if (!form.category.trim()) {
      showToast('Category select karein.');
      return false;
    }

    if (!form.stock_quantity && Number(form.stock_quantity) !== 0) {
      showToast('Stock quantity डालें.');
      return false;
    }

    if (form.pricing_type === 'fixed' && !form.selling_price) {
      showToast('Fixed price ke liye selling price required hai.');
      return false;
    }

    if (isWholesale && toNumber(form.moq, 0) <= 0) {
      showToast('Wholesale me minimum quantity required hai.');
      return false;
    }

    return true;
  };

  const validateProductDraft = (draft) => {
    if (!draft?.name?.trim()) {
      showToast('Product name required hai.');
      return false;
    }

    if (!draft?.category?.trim()) {
      showToast('Category select karein.');
      return false;
    }

    if (!draft.stock_quantity && Number(draft.stock_quantity) !== 0) {
      showToast('Stock quantity डालें.');
      return false;
    }

    if (draft.pricing_type === 'fixed' && !draft.selling_price) {
      showToast('Fixed price ke liye selling price required hai.');
      return false;
    }

    if (isWholesale && toNumber(draft.moq, 0) <= 0) {
      showToast('Wholesale me minimum quantity required hai.');
      return false;
    }

    return true;
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();

    if (!validateProductForm()) return;

    try {
      setSavingProduct(true);

      const fd = new FormData();
      appendProductFormData(fd, form);

      const isEdit = Boolean(form.id);
      const url = isEdit
        ? `${apiUrl}/grocery/products/${form.id}`
        : `${apiUrl}/grocery/products`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.error || 'Product save failed.');
        return;
      }

      showToast(data.message || 'Product saved successfully.');
      resetForm();
      await fetchProducts();
      setActiveTab('stock');
    } catch (err) {
      console.error('handleSubmitProduct error:', err);
      showToast('Product save failed.');
    } finally {
      setSavingProduct(false);
    }
  };

  const startInlineEdit = (product) => {
    const draft = buildProductDraft(product);

    revokeInlinePreviewIfBlob();

    setInlineEditId(product.id);
    setInlineEditForm(draft);
    setInlinePreviewUrl(getImageUrl(draft.existing_image));
    setMoreEditOpen(false);
  };

  const cancelInlineEdit = () => {
    revokeInlinePreviewIfBlob();

    setInlineEditId(null);
    setInlineEditForm(null);
    setInlinePreviewUrl('');
    setMoreEditOpen(false);
  };

  const openMoreEdit = (product) => {
    if (Number(inlineEditId) !== Number(product.id) || !inlineEditForm) {
      const draft = buildProductDraft(product);
      revokeInlinePreviewIfBlob();
      setInlineEditId(product.id);
      setInlineEditForm(draft);
      setInlinePreviewUrl(getImageUrl(draft.existing_image));
    }

    setMoreEditOpen(true);
  };

  const saveInlineProduct = async () => {
    if (!inlineEditForm?.id) return false;

    if (!validateProductDraft(inlineEditForm)) return false;

    try {
      setSavingInlineProduct(true);

      const fd = new FormData();
      appendProductFormData(fd, inlineEditForm);

      const response = await fetch(`${apiUrl}/grocery/products/${inlineEditForm.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.error || 'Product update failed.');
        return false;
      }

      showToast(data.message || 'Product updated successfully.');
      await fetchProducts();
      cancelInlineEdit();

      return true;
    } catch (err) {
      console.error('saveInlineProduct error:', err);
      showToast('Product update failed.');
      return false;
    } finally {
      setSavingInlineProduct(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    const ok = window.confirm('Ye product delete karna hai?');
    if (!ok) return;

    try {
      const response = await fetch(`${apiUrl}/grocery/products/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.error || 'Product delete failed.');
        return;
      }

      showToast(data.message || 'Product deleted.');
      await fetchProducts();
    } catch (err) {
      console.error('handleDeleteProduct error:', err);
      showToast('Product delete failed.');
    }
  };

  const saveSettings = async () => {
    try {
      setSavingSettings(true);

      const minEta = toNumber(settings.delivery_eta_min, 30);
      const maxEta = toNumber(settings.delivery_eta_max, 60);

      if (minEta > maxEta) {
        showToast('Minimum delivery time maximum se bada nahi hona chahiye.');
        return;
      }

      const payload = {
        grocery_mode: mode,
        enable_pickup: settings.enable_pickup ? 1 : 0,
        enable_home_delivery: settings.enable_home_delivery ? 1 : 0,
        allow_cod: settings.allow_cod ? 1 : 0,
        allow_online_payment: settings.allow_online_payment ? 1 : 0,
        min_order_amount: Math.max(0, toNumber(settings.min_order_amount, 0)),
        delivery_fee: Math.max(0, toNumber(settings.delivery_fee, 0)),
        packaging_fee: Math.max(0, toNumber(settings.packaging_fee, 0)),
        delivery_radius_km: Math.max(0, toNumber(settings.delivery_radius_km, 0)),
        delivery_eta_min: minEta,
        delivery_eta_max: maxEta,
        shop_open_time: normalizeTimeValue(settings.shop_open_time),
        shop_close_time: normalizeTimeValue(settings.shop_close_time),
      };

      const response = await fetch(`${apiUrl}/grocery-order-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.error || 'Settings save failed.');
        return;
      }

      showToast(data.message || 'Settings saved.');
      await fetchSettings();
    } catch (err) {
      console.error('saveSettings error:', err);
      showToast('Settings save failed.');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveOffer = async (e) => {
    e.preventDefault();

    if (!offerForm.title.trim()) {
      showToast('Offer name required hai.');
      return;
    }

    if (!offerForm.discount_value) {
      showToast('Discount value required hai.');
      return;
    }

    try {
      setSavingOffer(true);

      const isEdit = Boolean(offerForm.id);
      const url = isEdit
        ? `${apiUrl}/grocery-offers/${offerForm.id}`
        : `${apiUrl}/grocery-offers`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          grocery_mode: mode,
          ...offerForm,
          is_active: offerForm.is_active ? 1 : 0,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.error || 'Offer save failed.');
        return;
      }

      showToast(data.message || 'Offer saved.');
      resetOfferForm();
      await fetchOffers();
    } catch (err) {
      console.error('saveOffer error:', err);
      showToast('Offer save failed.');
    } finally {
      setSavingOffer(false);
    }
  };

  const editOffer = (offer) => {
    setOfferForm({
      id: offer.id,
      title: offer.title || '',
      offer_type: offer.offer_type || 'custom',
      category: offer.category || '',
      min_quantity: offer.min_quantity || '',
      min_purchase_amount: offer.min_purchase_amount || '',
      discount_value: offer.discount_value || '',
      description: offer.description || '',
      is_active: Boolean(Number(offer.is_active)),
    });

    setActiveTab('offers');
  };

  const deleteOffer = async (id) => {
    const ok = window.confirm('Offer delete karna hai?');
    if (!ok) return;

    try {
      const response = await fetch(`${apiUrl}/grocery-offers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.error || 'Offer delete failed.');
        return;
      }

      showToast(data.message || 'Offer deleted.');
      await fetchOffers();
    } catch (err) {
      console.error('deleteOffer error:', err);
      showToast('Offer delete failed.');
    }
  };

  const renderPublicPrice = (product) => {
    if (product.pricing_type === 'inquiry') return 'Price on Request';
    if (product.pricing_type === 'hidden' || !Number(product.show_price)) return 'Contact for Price';
    if (product.selling_price === null || product.selling_price === undefined || product.selling_price === '') {
      return 'Price Not Set';
    }
    return formatMoney(product.selling_price);
  };

  const getStockClass = (stock) => {
    const qty = toNumber(stock, 0);
    if (qty <= 0) return 'danger';
    if (qty <= 10) return 'warning';
    return 'success';
  };

  const getStockLabel = (stock) => {
    const qty = toNumber(stock, 0);
    if (qty <= 0) return 'Out';
    if (qty <= 10) return 'Low';
    return 'In Stock';
  };

  const openStockList = (filterType) => {
    setStockQuickFilter(filterType);
    setSearch('');
    setShowTrendingOnly(false);
    setActiveTab('stock');
  };

  if (!token) {
    return (
      <div className="grocery-login-box">
        <h2>Login Required</h2>
        <p>You need to login first to manage grocery products.</p>
        <button type="button" onClick={() => navigate('/login')}>
          Go to Login
        </button>
      </div>
    );
  }

  const renderSidebar = () => (
    <aside className="gm-sidebar">
      <div className="gm-sidebar-brand">
        <div className="gm-logo">🛒</div>
        <div>
          <strong>Grocery</strong>
          <span>Manager</span>
        </div>
      </div>

      <nav className="gm-side-nav">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={activeTab === item.key ? 'active' : ''}
            onClick={() => setActiveTab(item.key)}
          >
            <span>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </nav>

      <div className="gm-sidebar-footer">
        <span>{modeLabel} Mode</span>
      </div>
    </aside>
  );

  const renderMobileBottomNav = () => (
    <nav className="gm-mobile-nav">
      {NAV_ITEMS.slice(0, 5).map((item) => (
        <button
          key={item.key}
          type="button"
          className={activeTab === item.key ? 'active' : ''}
          onClick={() => setActiveTab(item.key)}
        >
          <span>{item.icon}</span>
          <small>{item.label}</small>
        </button>
      ))}
    </nav>
  );

  const renderTopHeader = () => (
    <header className="gm-top-header">
      <div>
        <span className={`gm-mode-badge ${isWholesale ? 'wholesale' : 'retail'}`}>
          {modeLabel} Grocery
        </span>
        <h1>{title || 'Grocery Manager'}</h1>
        <p>
          Simple tabs, big buttons, clear labels, aur kam confusion wala grocery panel.
        </p>
      </div>

      <div className="gm-header-actions">
        <button type="button" className="gm-light-btn" onClick={fetchProducts}>
          Refresh
        </button>
        <button type="button" className="gm-primary-btn" onClick={() => setActiveTab('products')}>
          + Add Product
        </button>
      </div>
    </header>
  );

  const renderSummaryCards = () => (
    <div className="gm-summary-grid">
      <button
        type="button"
        className="gm-summary-card gm-summary-click-card blue"
        onClick={() => openStockList('all')}
      >
        <span>Products</span>
        <strong>{summary.totalProducts}</strong>
        <small>Total items</small>
      </button>

      <button
        type="button"
        className="gm-summary-card gm-summary-click-card green"
        onClick={() => openStockList('active')}
      >
        <span>Active</span>
        <strong>{summary.activeProducts}</strong>
        <small>Visible products</small>
      </button>

      <button
        type="button"
        className="gm-summary-card gm-summary-click-card orange"
        onClick={() => openStockList('low')}
      >
        <span>Low Stock</span>
        <strong>{summary.lowStockProducts}</strong>
        <small>Need refill</small>
      </button>

      <button
        type="button"
        className="gm-summary-card gm-summary-click-card purple"
        onClick={() => setActiveTab('offers')}
      >
        <span>Offers</span>
        <strong>{summary.activeOffers}</strong>
        <small>Running</small>
      </button>
    </div>
  );

  const renderHome = () => (
    <div className="gm-home-grid">
      <section className="gm-card gm-home-main-card">
        <div className="gm-card-head">
          <div>
            <h2>Hello, Shop Owner 👋</h2>
            <p>Apni grocery shop ko simple tarike se manage karein.</p>
          </div>
        </div>

        {renderSummaryCards()}

        <div className="gm-quick-actions">
          <button type="button" onClick={() => setActiveTab('products')}>
            <span>📦</span>
            Add Product
          </button>

          <button type="button" onClick={() => openStockList('all')}>
            <span>📋</span>
            Stock List
          </button>

          <button type="button" onClick={() => setActiveTab('settings')}>
            <span>🚚</span>
            Delivery
          </button>

          <button type="button" onClick={() => setActiveTab('offers')}>
            <span>🏷️</span>
            Offers
          </button>
        </div>

        <div className="gm-tip-box">
          <strong>💡 Tip</strong>
          <p>Stock updated rakhen. Customer ko product aur price clear dikhega.</p>
        </div>
      </section>

      <section className="gm-card">
        <div className="gm-card-head">
          <div>
            <h3>Low Stock Alerts</h3>
            <p>Jaldi refill karne wale products</p>
          </div>
          <button type="button" className="gm-text-btn" onClick={() => openStockList('low')}>
            View All
          </button>
        </div>

        <div className="gm-mini-list">
          {lowStockProducts.length === 0 ? (
            <div className="gm-empty-state">Low stock product nahi hai.</div>
          ) : (
            lowStockProducts.map((product) => (
              <div key={product.id} className="gm-mini-product-row">
                <div className="gm-mini-img">
                  {product.image_url || product.image ? (
                    <img src={getImageUrl(product.image_url || product.image)} alt={product.name} />
                  ) : (
                    <span>📦</span>
                  )}
                </div>

                <div>
                  <strong>{product.name}</strong>
                  <span>{product.pack_size || product.unit_type || '-'}</span>
                </div>

                <b>{toNumber(product.stock_quantity, 0)} pcs</b>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );

  const renderProductForm = () => (
    <form className="gm-card gm-product-form" onSubmit={handleSubmitProduct}>
      <div className="gm-card-head">
        <div>
          <h2>{form.id ? 'Edit Product' : 'Add Product'}</h2>
          <p>Kam fields, easy product entry.</p>
        </div>

        {voiceSupported ? (
          <button type="button" className="gm-light-btn" onClick={handleVoiceInput}>
            {isListening ? 'Listening...' : '🎤 Voice'}
          </button>
        ) : null}
      </div>

      <div className="gm-photo-box" onClick={() => fileInputRef.current?.click()}>
        {previewUrl ? (
          <img src={previewUrl} alt="Product Preview" />
        ) : (
          <div>
            <span>📷</span>
            <strong>Add Photo</strong>
            <small>Tap to upload</small>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/"
          name="image"
          onChange={handleChange}
          hidden
        />
      </div>

      <div className="gm-form-grid simple">
        <label>
          <span>Product Name *</span>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Enter product name"
            required
          />
        </label>

        <label>
          <span>Category *</span>
          <select name="category" value={form.category} onChange={handleChange} required>
            <option value="">Select category</option>
            {CATEGORY_OPTIONS.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Price ₹</span>
          <input
            name="selling_price"
            type="number"
            step="0.01"
            value={form.selling_price}
            onChange={handleChange}
            placeholder="Enter price"
            disabled={form.pricing_type !== 'fixed'}
          />
        </label>

        <label>
          <span>Stock Quantity *</span>
          <input
            name="stock_quantity"
            type="number"
            value={form.stock_quantity}
            onChange={handleChange}
            placeholder="Enter stock"
            required
          />
        </label>
      </div>

      <button
        type="button"
        className="gm-show-more-btn"
        onClick={() => setShowMoreFields((prev) => !prev)}
      >
        {showMoreFields ? 'Hide More Fields' : '+ More Fields'}
      </button>

      {showMoreFields ? (
        <div className="gm-form-grid advanced">
          <label>
            <span>Brand</span>
            <input
              name="brand"
              value={form.brand}
              onChange={handleChange}
              placeholder="Brand name"
            />
          </label>

          <label>
            <span>Unit</span>
            <select name="unit_type" value={form.unit_type} onChange={handleChange}>
              <option value="">Select unit</option>
              {UNIT_OPTIONS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Pack Size</span>
            <input
              name="pack_size"
              value={form.pack_size}
              onChange={handleChange}
              placeholder="1kg / 25kg bag / carton"
            />
          </label>

          <label>
            <span>Cost Price</span>
            <input
              name="cost_price"
              type="number"
              step="0.01"
              value={form.cost_price}
              onChange={handleChange}
              placeholder="Optional"
            />
          </label>

          <label>
            <span>Price Visibility</span>
            <select name="pricing_type" value={form.pricing_type} onChange={handleChange}>
              <option value="fixed">Price Dikhana</option>
              <option value="hidden">Price Chhupana</option>
              <option value="inquiry">Customer Puchhega</option>
            </select>
          </label>

          <label>
            <span>Minimum Qty</span>
            <input
              name="moq"
              type="number"
              value={form.moq}
              onChange={handleChange}
              placeholder="1"
            />
          </label>

          <label className="gm-check-card">
            <input
              name="show_price"
              type="checkbox"
              checked={Boolean(form.show_price)}
              onChange={handleChange}
              disabled={form.pricing_type !== 'fixed'}
            />
            <span>Customer ko price dikhe</span>
          </label>

          <label className="gm-check-card">
            <input
              name="trend_item"
              type="checkbox"
              checked={Boolean(form.trend_item)}
              onChange={handleChange}
            />
            <span>Top Product</span>
          </label>

          <label className="gm-check-card">
            <input
              name="is_active"
              type="checkbox"
              checked={Boolean(form.is_active)}
              onChange={handleChange}
            />
            <span>Product Chalu Hai</span>
          </label>

          <label className="gm-full-field">
            <span>Description</span>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Optional product note"
            />
          </label>
        </div>
      ) : null}

      <div className="gm-form-actions">
        <button type="submit" className="gm-primary-btn" disabled={savingProduct}>
          {savingProduct ? 'Saving...' : form.id ? 'Update Product' : 'Save Product'}
        </button>

        <button type="button" className="gm-light-btn" onClick={resetForm}>
          Clear
        </button>
      </div>
    </form>
  );

  const renderProductsTab = () => (
    <div className="gm-two-column-layout">
      {renderProductForm()}

      <section className="gm-card gm-side-help-card">
        <h3>{modeLabel} Tips</h3>

        {isWholesale ? (
          <>
            <p>Wholesale me MOQ aur bulk price important hai.</p>
            <div className="gm-help-point">Minimum Qty ko clear rakhein.</div>
            <div className="gm-help-point">Hidden price / inquiry price use kar sakte hain.</div>
            <div className="gm-help-point">Stock quantity always updated rakhein.</div>
          </>
        ) : (
          <>
            <p>Retail me price aur stock customer ko clear dikhna chahiye.</p>
            <div className="gm-help-point">Product name simple rakhein.</div>
            <div className="gm-help-point">Price visible rakhein.</div>
            <div className="gm-help-point">Photo add karna customer ke liye helpful hai.</div>
          </>
        )}

        <button type="button" className="gm-primary-btn" onClick={() => openStockList('all')}>
          View Stock List
        </button>
      </section>
    </div>
  );

  const renderInlineImagePicker = () => (
    <label className="gm-inline-image-edit">
      {inlinePreviewUrl ? (
        <img src={inlinePreviewUrl} alt="Product" />
      ) : (
        <span>📷</span>
      )}

      <small>Photo</small>

      <input
        type="file"
        accept="image/"
        onChange={(e) => handleInlineFileChange(e.target.files?.[0])}
        hidden
      />
    </label>
  );

  const renderDesktopStockRow = (product) => {
    const stockClass = getStockClass(product.stock_quantity);
    const isEditing = Number(inlineEditId) === Number(product.id) && inlineEditForm;

    if (isEditing) {
      return (
        <div className="gm-stock-row gm-stock-row-editing" key={product.id}>
          <div className="gm-product-info gm-inline-product-info">
            {renderInlineImagePicker()}

            <div className="gm-inline-product-fields">
              <input
                className="gm-inline-input"
                value={inlineEditForm.name}
                onChange={(e) => handleInlineChange('name', e.target.value)}
                placeholder="Product name"
              />
              <small>
                {[inlineEditForm.pack_size, inlineEditForm.unit_type, inlineEditForm.category]
                  .filter(Boolean)
                  .join(' • ') || 'More Edit se details set karein'}
              </small>
            </div>
          </div>

          <input
            className="gm-inline-input gm-inline-price-input"
            type="number"
            value={inlineEditForm.selling_price}
            onChange={(e) => handleInlineChange('selling_price', e.target.value)}
            placeholder="Price"
            disabled={inlineEditForm.pricing_type !== 'fixed'}
          />

          <input
            className="gm-inline-input gm-inline-stock-input"
            type="number"
            value={inlineEditForm.stock_quantity}
            onChange={(e) => handleInlineChange('stock_quantity', e.target.value)}
            placeholder="Stock"
          />

          <select
            className="gm-inline-input"
            value={inlineEditForm.is_active ? '1' : '0'}
            onChange={(e) => handleInlineChange('is_active', e.target.value === '1')}
          >
            <option value="1">Active</option>
            <option value="0">Off</option>
          </select>

          <div className="gm-row-actions gm-inline-row-actions">
            <button type="button" onClick={saveInlineProduct} disabled={savingInlineProduct}>
              {savingInlineProduct ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={() => openMoreEdit(product)}>
              More Edit
            </button>
            <button type="button" className="danger" onClick={cancelInlineEdit}>
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="gm-stock-row" key={product.id}>
        <div className="gm-product-info">
          <div className="gm-product-img">
            {product.image_url || product.image ? (
              <img src={getImageUrl(product.image_url || product.image)} alt={product.name} />
            ) : (
              <span>📦</span>
            )}
          </div>

          <div>
            <strong>{product.name}</strong>
            <small>
              {[product.pack_size, product.unit_type, product.category]
                .filter(Boolean)
                .join(' • ') || '-'}
            </small>
          </div>
        </div>

        <span className="gm-price-text">{renderPublicPrice(product)}</span>

        <span className={`gm-stock-number ${stockClass}`}>
          {toNumber(product.stock_quantity, 0)} pcs
        </span>

        <span className={`gm-status-pill ${stockClass}`}>
          {getStockLabel(product.stock_quantity)}
        </span>

        <div className="gm-row-actions">
          <button type="button" onClick={() => startInlineEdit(product)}>
            Edit
          </button>

          <button type="button" className="danger" onClick={() => handleDeleteProduct(product.id)}>
            Delete
          </button>
        </div>
      </div>
    );
  };

  const renderMobileStockCard = (product) => {
    const stockClass = getStockClass(product.stock_quantity);
    const isEditing = Number(inlineEditId) === Number(product.id) && inlineEditForm;

    if (isEditing) {
      return (
        <div className="gm-mobile-product-card gm-mobile-product-card-editing" key={product.id}>
          {renderInlineImagePicker()}

          <div className="gm-mobile-product-body gm-mobile-inline-body">
            <input
              className="gm-inline-input"
              value={inlineEditForm.name}
              onChange={(e) => handleInlineChange('name', e.target.value)}
              placeholder="Product name"
            />

            <div className="gm-mobile-inline-grid">
              <input
                className="gm-inline-input"
                type="number"
                value={inlineEditForm.selling_price}
                onChange={(e) => handleInlineChange('selling_price', e.target.value)}
                placeholder="Price"
                disabled={inlineEditForm.pricing_type !== 'fixed'}
              />

              <input
                className="gm-inline-input"
                type="number"
                value={inlineEditForm.stock_quantity}
                onChange={(e) => handleInlineChange('stock_quantity', e.target.value)}
                placeholder="Stock"
              />
            </div>

            <select
              className="gm-inline-input"
              value={inlineEditForm.is_active ? '1' : '0'}
              onChange={(e) => handleInlineChange('is_active', e.target.value === '1')}
            >
              <option value="1">Active</option>
              <option value="0">Off</option>
            </select>

            <div className="gm-mobile-inline-actions">
              <button type="button" onClick={saveInlineProduct} disabled={savingInlineProduct}>
                {savingInlineProduct ? 'Saving...' : 'Save'}
              </button>
              <button type="button" onClick={() => openMoreEdit(product)}>
                More
              </button>
              <button type="button" className="danger" onClick={cancelInlineEdit}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="gm-mobile-product-card" key={product.id}>
        <div className="gm-product-img">
          {product.image_url || product.image ? (
            <img src={getImageUrl(product.image_url || product.image)} alt={product.name} />
          ) : (
            <span>📦</span>
          )}
        </div>

        <div className="gm-mobile-product-body">
          <strong>{product.name}</strong>
          <small>{product.pack_size || product.unit_type || product.category || '-'}</small>
          <span>{renderPublicPrice(product)}</span>
        </div>

        <div className="gm-mobile-stock-side">
          <b className={stockClass}>{toNumber(product.stock_quantity, 0)}</b>
          <small>pcs</small>
          <button type="button" onClick={() => startInlineEdit(product)}>
            Edit
          </button>
        </div>
      </div>
    );
  };

  const renderMoreEditModal = () => {
    if (!moreEditOpen || !inlineEditForm) return null;

    return (
      <div className="gm-modal-overlay">
        <div className="gm-modal-card">
          <div className="gm-modal-head">
            <div>
              <h2>More Edit</h2>
              <p>{inlineEditForm.name || 'Product details'}</p>
            </div>

            <button type="button" onClick={() => setMoreEditOpen(false)}>
              ×
            </button>
          </div>

          <div className="gm-more-edit-photo-row">
            {renderInlineImagePicker()}
            <div>
              <strong>Product Photo</strong>
              <p>Photo change karne ke liye image par click karein.</p>
            </div>
          </div>

          <div className="gm-form-grid advanced gm-inline-more-grid">
            <label>
              <span>Category *</span>
              <select
                value={inlineEditForm.category}
                onChange={(e) => handleInlineChange('category', e.target.value)}
              >
                <option value="">Select category</option>
                {CATEGORY_OPTIONS.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Brand</span>
              <input
                value={inlineEditForm.brand}
                onChange={(e) => handleInlineChange('brand', e.target.value)}
                placeholder="Brand name"
              />
            </label>

            <label>
              <span>Unit</span>
              <select
                value={inlineEditForm.unit_type}
                onChange={(e) => handleInlineChange('unit_type', e.target.value)}
              >
                <option value="">Select unit</option>
                {UNIT_OPTIONS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Pack Size</span>
              <input
                value={inlineEditForm.pack_size}
                onChange={(e) => handleInlineChange('pack_size', e.target.value)}
                placeholder="1kg / 25g / carton"
              />
            </label>

            <label>
              <span>Cost Price</span>
              <input
                type="number"
                step="0.01"
                value={inlineEditForm.cost_price}
                onChange={(e) => handleInlineChange('cost_price', e.target.value)}
                placeholder="Optional"
              />
            </label>

            <label>
              <span>Price Visibility</span>
              <select
                value={inlineEditForm.pricing_type}
                onChange={(e) => handleInlineChange('pricing_type', e.target.value)}
              >
                <option value="fixed">Price Dikhana</option>
                <option value="hidden">Price Chhupana</option>
                <option value="inquiry">Customer Puchhega</option>
              </select>
            </label>

            <label>
              <span>Minimum Qty</span>
              <input
                type="number"
                value={inlineEditForm.moq}
                onChange={(e) => handleInlineChange('moq', e.target.value)}
                placeholder="1"
              />
            </label>

            <label className="gm-check-card">
              <input
                type="checkbox"
                checked={Boolean(inlineEditForm.show_price)}
                onChange={(e) => handleInlineChange('show_price', e.target.checked)}
                disabled={inlineEditForm.pricing_type !== 'fixed'}
              />
              <span>Customer ko price dikhe</span>
            </label>

            <label className="gm-check-card">
              <input
                type="checkbox"
                checked={Boolean(inlineEditForm.trend_item)}
                onChange={(e) => handleInlineChange('trend_item', e.target.checked)}
              />
              <span>Top Product</span>
            </label>

            <label className="gm-check-card">
              <input
                type="checkbox"
                checked={Boolean(inlineEditForm.is_active)}
                onChange={(e) => handleInlineChange('is_active', e.target.checked)}
              />
              <span>Product Chalu Hai</span>
            </label>

            <label className="gm-full-field">
              <span>Description</span>
              <textarea
                value={inlineEditForm.description}
                onChange={(e) => handleInlineChange('description', e.target.value)}
                placeholder="Optional product note"
              />
            </label>
          </div>

          <div className="gm-modal-actions">
            <button
              type="button"
              className="gm-light-btn"
              onClick={() => setMoreEditOpen(false)}
            >
              Close
            </button>

            <button
              type="button"
              className="gm-primary-btn"
              onClick={async () => {
                const ok = await saveInlineProduct();
                if (ok) setMoreEditOpen(false);
              }}
              disabled={savingInlineProduct}
            >
              {savingInlineProduct ? 'Saving...' : 'Save Product'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStockList = () => (
    <section className="gm-card gm-stock-card">
      <div className="gm-card-head">
        <div>
          <h2>Stock List</h2>
          <p>Yahin par product name, photo, price, stock aur status edit karein.</p>
        </div>

        <button type="button" className="gm-primary-btn" onClick={() => setActiveTab('products')}>
          + Add Product
        </button>
      </div>

      <div className="gm-stock-toolbar">
        <input
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button type="button" onClick={() => setShowTrendingOnly((prev) => !prev)}>
          {showTrendingOnly ? 'Show All' : 'Top Products'}
        </button>

        <button type="button" onClick={fetchProducts}>
          Refresh
        </button>
      </div>

      {stockQuickFilter !== 'all' ? (
        <div className="gm-stock-filter-note">
          <span>
            Showing:{' '}
            <strong>
              {stockQuickFilter === 'active' ? 'Active Products' : 'Low Stock Products'}
            </strong>
          </span>

          <button
            type="button"
            onClick={() => {
              setStockQuickFilter('all');
              setSearch('');
              setShowTrendingOnly(false);
            }}
          >
            Show All
          </button>
        </div>
      ) : null}

      {loadingProducts ? (
        <div className="gm-empty-state">Loading products...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="gm-empty-state">No products found.</div>
      ) : (
        <>
          <div className="gm-stock-table">
            <div className="gm-stock-table-head">
              <span>Product</span>
              <span>Price</span>
              <span>Stock</span>
              <span>Status</span>
              <span>Action</span>
            </div>

            {filteredProducts.map(renderDesktopStockRow)}
          </div>

          <div className="gm-mobile-stock-list">
            {filteredProducts.map(renderMobileStockCard)}
          </div>
        </>
      )}

      {renderMoreEditModal()}
    </section>
  );

  const renderSettingsTab = () => {
    const TimePickerField = ({ label, value, onChange }) => {
      const parts = time24ToParts(value);

      const safeParts = {
        hour: parts.hour || '9',
        minute: parts.minute || '00',
        period: parts.period || 'AM',
      };

      const updatePart = (key, nextValue) => {
        const nextParts = {
          ...safeParts,
          [key]: nextValue,
        };

        const nextTime = partsTo24Time(nextParts);
        onChange(nextTime);
      };

      const clearTime = () => {
        onChange('');
      };

      return (
        <div className="gm-time-field">
          <div className="gm-time-label-row">
            <span>{label}</span>
            <button type="button" onClick={clearTime}>
              Clear
            </button>
          </div>

          <div className="gm-time-select-row">
            <select
              value={parts.hour || ''}
              onChange={(e) => updatePart('hour', e.target.value)}
            >
              <option value="">Hour</option>
              {Array.from({ length: 12 }, (_, index) => String(index + 1)).map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>

            <select
              value={parts.minute || '00'}
              onChange={(e) => updatePart('minute', e.target.value)}
            >
              {Array.from({ length: 60 }, (_, index) => pad2(index)).map((minute) => (
                <option key={minute} value={minute}>
                  {minute}
                </option>
              ))}
            </select>

            <select
              value={parts.period || 'AM'}
              onChange={(e) => updatePart('period', e.target.value)}
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>

          <small>Selected: {formatTime12(value)}</small>
        </div>
      );
    };

    return (
      <div className="gm-two-column-layout">
      <section className="gm-card">
        <div className="gm-card-head">
          <div>
            <h2>Delivery Settings</h2>
            <p>Simple ON/OFF settings.</p>
          </div>
        </div>

        <div className="gm-toggle-list">
          <label className="gm-toggle-card">
            <div>
              <strong>Shop Pickup</strong>
              <span>Customer dukan se pickup karega</span>
            </div>
            <input
              type="checkbox"
              checked={Boolean(settings.enable_pickup)}
              onChange={(e) => handleSettingsChange('enable_pickup', e.target.checked)}
            />
          </label>

          <label className="gm-toggle-card">
            <div>
              <strong>Home Delivery</strong>
              <span>Customer ke ghar delivery</span>
            </div>
            <input
              type="checkbox"
              checked={Boolean(settings.enable_home_delivery)}
              onChange={(e) => handleSettingsChange('enable_home_delivery', e.target.checked)}
            />
          </label>

          <label className="gm-toggle-card">
            <div>
              <strong>Cash / COD</strong>
              <span>Cash ya delivery par payment</span>
            </div>
            <input
              type="checkbox"
              checked={Boolean(settings.allow_cod)}
              onChange={(e) => handleSettingsChange('allow_cod', e.target.checked)}
            />
          </label>

          <label className="gm-toggle-card">
            <div>
              <strong>Online Payment</strong>
              <span>UPI / Razorpay / online payment</span>
            </div>
            <input
              type="checkbox"
              checked={Boolean(settings.allow_online_payment)}
              onChange={(e) => handleSettingsChange('allow_online_payment', e.target.checked)}
            />
          </label>
        </div>

        <button
          type="button"
          className="gm-show-more-btn"
          onClick={() => setShowAdvancedSettings((prev) => !prev)}
        >
          {showAdvancedSettings ? 'Hide Advanced Settings' : '+ Advanced Delivery Settings'}
        </button>

        {showAdvancedSettings ? (
          <div className="gm-form-grid advanced settings">
            <label>
              <span>Minimum Order ₹</span>
              <input
                type="number"
                value={settings.min_order_amount}
                onChange={(e) => handleSettingsChange('min_order_amount', e.target.value)}
              />
            </label>

            <label>
              <span>Delivery Charge ₹</span>
              <input
                type="number"
                value={settings.delivery_fee}
                onChange={(e) => handleSettingsChange('delivery_fee', e.target.value)}
              />
            </label>

            <label>
              <span>Packing Charge ₹</span>
              <input
                type="number"
                value={settings.packaging_fee}
                onChange={(e) => handleSettingsChange('packaging_fee', e.target.value)}
              />
            </label>

            <label>
              <span>Delivery KM</span>
              <input
                type="number"
                value={settings.delivery_radius_km}
                onChange={(e) => handleSettingsChange('delivery_radius_km', e.target.value)}
              />
            </label>

            <label>
              <span>ETA Min</span>
              <input
                type="number"
                value={settings.delivery_eta_min}
                onChange={(e) => handleSettingsChange('delivery_eta_min', e.target.value)}
              />
            </label>

            <label>
              <span>ETA Max</span>
              <input
                type="number"
                value={settings.delivery_eta_max}
                onChange={(e) => handleSettingsChange('delivery_eta_max', e.target.value)}
              />
            </label>

            <TimePickerField
              label="Shop Open"
              value={settings.shop_open_time}
              onChange={(nextTime) => handleSettingsChange('shop_open_time', nextTime)}
            />

            <TimePickerField
              label="Shop Close"
              value={settings.shop_close_time}
              onChange={(nextTime) => handleSettingsChange('shop_close_time', nextTime)}
            />

            <div className="gm-time-preview-box">
              <span>Shop Timing Preview</span>
              <b>{getShopTimingPreview(settings.shop_open_time, settings.shop_close_time)}</b>
            </div>
          </div>
        ) : null}

        <div className="gm-form-actions">
          <button type="button" className="gm-primary-btn" onClick={saveSettings} disabled={savingSettings}>
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </section>

      <section className="gm-card gm-side-help-card">
        <h3>Settings Preview</h3>

        <div className="gm-preview-list">
          <div>
            <span>Pickup</span>
            <b>{settings.enable_pickup ? 'ON' : 'OFF'}</b>
          </div>

          <div>
            <span>Delivery</span>
            <b>{settings.enable_home_delivery ? 'ON' : 'OFF'}</b>
          </div>

          <div>
            <span>COD</span>
            <b>{settings.allow_cod ? 'ON' : 'OFF'}</b>
          </div>

          <div>
            <span>Online</span>
            <b>{settings.allow_online_payment ? 'ON' : 'OFF'}</b>
          </div>

          <div>
            <span>Timing</span>
            <b>{getShopTimingPreview(settings.shop_open_time, settings.shop_close_time)}</b>
          </div>
        </div>
      </section>
    </div>
    );
  };

  const renderOffersTab = () => (
    <div className="gm-two-column-layout">
      <form className="gm-card" onSubmit={saveOffer}>
        <div className="gm-card-head">
          <div>
            <h2>{offerForm.id ? 'Edit Offer' : 'Create Offer'}</h2>
            <p>Simple offer banao.</p>
          </div>
        </div>

        <div className="gm-form-grid simple">
          <label>
            <span>Offer Name *</span>
            <input
              value={offerForm.title}
              onChange={(e) => handleOfferChange('title', e.target.value)}
              placeholder="Summer Sale"
              required
            />
          </label>

          <label>
            <span>Offer Type</span>
            <select
              value={offerForm.offer_type}
              onChange={(e) => handleOfferChange('offer_type', e.target.value)}
            >
              <option value="custom">Custom</option>
              <option value="percent">Percent Discount</option>
              <option value="flat">Flat Discount</option>
              <option value="bulk">Bulk Offer</option>
            </select>
          </label>

          <label>
            <span>Discount *</span>
            <input
              type="number"
              value={offerForm.discount_value}
              onChange={(e) => handleOfferChange('discount_value', e.target.value)}
              placeholder="10"
              required
            />
          </label>

          <label>
            <span>Minimum Order ₹</span>
            <input
              type="number"
              value={offerForm.min_purchase_amount}
              onChange={(e) => handleOfferChange('min_purchase_amount', e.target.value)}
              placeholder="500"
            />
          </label>
        </div>

        <button
          type="button"
          className="gm-show-more-btn"
          onClick={() => setShowMoreFields((prev) => !prev)}
        >
          {showMoreFields ? 'Hide Offer Details' : '+ More Offer Details'}
        </button>

        {showMoreFields ? (
          <div className="gm-form-grid advanced">
            <label>
              <span>Category</span>
              <select
                value={offerForm.category}
                onChange={(e) => handleOfferChange('category', e.target.value)}
              >
                <option value="">All categories</option>
                {CATEGORY_OPTIONS.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Minimum Qty</span>
              <input
                type="number"
                value={offerForm.min_quantity}
                onChange={(e) => handleOfferChange('min_quantity', e.target.value)}
                placeholder="5"
              />
            </label>

            <label className="gm-check-card">
              <input
                type="checkbox"
                checked={Boolean(offerForm.is_active)}
                onChange={(e) => handleOfferChange('is_active', e.target.checked)}
              />
              <span>Offer Active</span>
            </label>

            <label className="gm-full-field">
              <span>Description</span>
              <textarea
                value={offerForm.description}
                onChange={(e) => handleOfferChange('description', e.target.value)}
                placeholder="Optional offer note"
              />
            </label>
          </div>
        ) : null}

        <div className="gm-form-actions">
          <button type="submit" className="gm-primary-btn" disabled={savingOffer}>
            {savingOffer ? 'Saving...' : offerForm.id ? 'Update Offer' : 'Save Offer'}
          </button>

          <button type="button" className="gm-light-btn" onClick={resetOfferForm}>
            Clear
          </button>
        </div>
      </form>

      <section className="gm-card">
        <div className="gm-card-head">
          <div>
            <h2>Active Offers</h2>
            <p>Running offers list.</p>
          </div>
        </div>

        <div className="gm-offer-list">
          {offers.length === 0 ? (
            <div className="gm-empty-state">Offer nahi hai.</div>
          ) : (
            offers.map((offer) => (
              <div key={offer.id} className="gm-offer-card">
                <div>
                  <span className={Number(offer.is_active) === 1 ? 'gm-active-badge' : 'gm-inactive-badge'}>
                    {Number(offer.is_active) === 1 ? 'Active' : 'Off'}
                  </span>
                  <h3>{offer.title}</h3>
                  <strong>{offer.discount_value}% OFF</strong>
                  <p>
                    Min Order: {offer.min_purchase_amount ? formatMoney(offer.min_purchase_amount) : '-'}
                  </p>
                </div>

                <div className="gm-offer-actions">
                  <button type="button" onClick={() => editOffer(offer)}>
                    Edit
                  </button>
                  <button type="button" className="danger" onClick={() => deleteOffer(offer.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );

  const renderMainContent = () => {
    if (activeTab === 'home') return renderHome();
    if (activeTab === 'products') return renderProductsTab();
    if (activeTab === 'stock') return renderStockList();
    if (activeTab === 'settings') return renderSettingsTab();
    if (activeTab === 'offers') return renderOffersTab();

    return renderHome();
  };

  return (
    <div className="gm-page-shell">
      {renderSidebar()}

      <main className="gm-main">
        {renderTopHeader()}

        {toast ? <div className="gm-toast">{toast}</div> : null}

        {renderMainContent()}
      </main>

      {renderMobileBottomNav()}
    </div>
  );
}

export default GroceryManager;
